import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions,
    CircularProgress, Chip, Divider, List, ListItem, ListItemText, ListItemIcon
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import {
    Backup as BackupIcon,
    Restore as RestoreIcon,
    Info as InfoIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Schedule as PendingIcon,
    Storage as StorageIcon,
    Dns as DnsIcon
} from '@material-ui/icons';
import api from '../../api/axios';
import Template from '../templates/Template';
import { dateTimeToStringID } from '../../repo/utils';

const BackupPage = () => {
    const [backups, setBackups] = useState([]);
    const [currentStatus, setCurrentStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);
    const [infoModal, setInfoModal] = useState({ open: false, data: null });
    const [restoreDialog, setRestoreDialog] = useState({ open: false, backup: null });

    const fetchBackups = async () => {
        setLoading(true);
        try {
            const [backupRes, statusRes] = await Promise.all([
                api.get('/backup'),
                api.get('/backup/current')
            ]);
            setBackups(backupRes.data.document);
            setCurrentStatus(statusRes.data.document);
        } catch (err) {
            setError('Failed to fetch backup data');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBackups();
    }, []);

    const handleBackupNow = async () => {
        setActionLoading(true);
        setError(null);
        try {
            await api.post('/backup', {});
            fetchBackups();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to trigger backup');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRestore = async () => {
        if (!restoreDialog.backup) return;
        setRestoreDialog({ ...restoreDialog, open: false });

        // Import and trigger maintenance mode immediately
        const { triggerMaintenanceMode } = await import('../../api/axios');
        triggerMaintenanceMode('Database sedang dipulihkan. Aplikasi akan dalam mode pemeliharaan selama proses berlangsung.');

        try {
            await api.post('/backup/restore', { id: restoreDialog.backup.id });
            // Don't need to do anything here - the maintenance modal will handle the UI
            // The user will click "Retry" when they want to check if restore is complete
        } catch (err) {
            // If error is not 503, show error message
            if (err.response?.status !== 503) {
                setError(err.response?.data?.message || 'Failed to restore database');
            }
            // If it's 503, the maintenance modal is already showing, so no need to do anything
        }
    };

    const formatSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getStatusChip = (status) => {
        switch (status) {
            case 'success': return <Chip size="small" icon={<SuccessIcon />} label="Success" color="primary" />;
            case 'pending': return <Chip size="small" icon={<PendingIcon />} label="Pending" color="secondary" />;
            case 'failed': return <Chip size="small" icon={<ErrorIcon />} label="Failed" color="default" />;
            default: return <Chip size="small" label={status} />;
        }
    };

    return (
        <Template title="Database Backup Management">
            <Box p={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h5">Database Backups</Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <BackupIcon />}
                        onClick={handleBackupNow}
                        disabled={actionLoading}
                    >
                        Backup Now
                    </Button>
                </Box>

                {error && <Alert severity="error" style={{ marginBottom: 24 }}>{error}</Alert>}

                {currentStatus && currentStatus.filename && (
                    <Paper style={{ padding: 16, marginBottom: 24, backgroundColor: '#f5f5f5', borderLeft: '4px solid #3f51b5' }}>
                        <Typography variant="subtitle2" color="textSecondary">Current Database Version (Last Restored)</Typography>
                        <Box display="flex" alignItems="center" mt={1}>
                            <StorageIcon style={{ marginRight: 8, color: '#3f51b5' }} />
                            <Typography variant="h6">{currentStatus.filename}</Typography>
                            <Box flexGrow={1} />
                            <Typography variant="caption">Restored on: {dateTimeToStringID(new Date(currentStatus.restored_at))}</Typography>
                        </Box>
                    </Paper>
                )}

                <TableContainer component={Paper}>
                    <Table size="medium">
                        <TableHead style={{ backgroundColor: '#eeeeee' }}>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Filename</TableCell>
                                <TableCell>Size</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={6} align="center"><CircularProgress style={{ margin: 24 }} /></TableCell></TableRow>
                            ) : !backups || backups.length === 0 ? (
                                <TableRow><TableCell colSpan={6} align="center">No backups found</TableCell></TableRow>
                            ) : (
                                backups.map((backup) => (
                                    <TableRow key={backup.id} hover>
                                        <TableCell>{dateTimeToStringID(new Date(backup.created_at))}</TableCell>
                                        <TableCell>{backup.filename}</TableCell>
                                        <TableCell>{formatSize(backup.size)}</TableCell>
                                        <TableCell><Chip size="small" label={backup.type} variant="outlined" /></TableCell>
                                        <TableCell>{getStatusChip(backup.status)}</TableCell>
                                        <TableCell align="center">
                                            <Box display="flex" justifyContent="center">
                                                <Tooltip title="View Validation Info">
                                                    <IconButton onClick={() => setInfoModal({ open: true, data: JSON.parse(backup.validation_info || '{}') })}>
                                                        <InfoIcon color="primary" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Restore from this backup">
                                                    <IconButton
                                                        color="secondary"
                                                        onClick={() => setRestoreDialog({ open: true, backup })}
                                                        disabled={backup.status !== 'success' || actionLoading}
                                                    >
                                                        <RestoreIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Validation Info Modal */}
                <Dialog open={infoModal.open} onClose={() => setInfoModal({ open: false, data: null })} maxWidth="md" fullWidth>
                    <DialogTitle>Backup Validation Details</DialogTitle>
                    <DialogContent dividers>
                        {infoModal.data && (
                            <Box>
                                <Typography variant="h6" gutterBottom>Domain Summary</Typography>
                                <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2} mb={3}>
                                    {Object.entries(infoModal.data.domain_summary || {}).map(([domain, info]) => (
                                        <Paper key={domain} variant="outlined" style={{ padding: 16 }}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>{domain}</Typography>
                                                {info.status === 'Complete' ? <SuccessIcon style={{ color: 'green' }} /> : <ErrorIcon style={{ color: 'red' }} />}
                                            </Box>
                                            <Typography variant="body2" color="textSecondary">{info.details}</Typography>
                                            <Typography variant="h6" mt={1}>{info.total_records.toLocaleString()} records</Typography>
                                        </Paper>
                                    ))}
                                </Box>
                                <Divider style={{ margin: '16px 0' }} />
                                <Typography variant="h6" gutterBottom>Technical Details</Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon><DnsIcon /></ListItemIcon>
                                        <ListItemText primary="Database Name" secondary={infoModal.data.technical?.database} />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><PendingIcon /></ListItemIcon>
                                        <ListItemText primary="Generated At" secondary={dateTimeToStringID(new Date(infoModal.data.technical?.timestamp))} />
                                    </ListItem>
                                </List>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setInfoModal({ open: false, data: null })}>Close</Button>
                    </DialogActions>
                </Dialog>

                {/* Restore Confirmation Dialog */}
                <Dialog open={restoreDialog.open} onClose={() => setRestoreDialog({ open: false, backup: null })}>
                    <DialogTitle>Confirm Restoration</DialogTitle>
                    <DialogContent>
                        <Alert severity="warning" style={{ marginBottom: 16 }}>
                            WARNING: This will overwrite the current live database with data from <strong>{restoreDialog.backup?.filename}</strong>. This action cannot be undone.
                        </Alert>
                        <Typography>Are you sure you want to proceed?</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRestoreDialog({ open: false, backup: null })}>Cancel</Button>
                        <Button onClick={handleRestore} color="secondary" variant="contained">Yes, Restore Now</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Template>
    );
};

export default BackupPage;
